import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';

// ─── 环境配置 ────────────────────────────────────────────────
// 通过环境变量配置后端地址，开发时可在 .env.local 设置 VITE_API_BASE_URL
const BASE_URL = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_BASE_URL) || '/api';

// ─── Token 管理 ───────────────────────────────────────────────
export const TOKEN_KEY = 'digital_employee_token';
export const REFRESH_TOKEN_KEY = 'digital_employee_refresh_token';

export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);
export const setToken = (token: string): void => localStorage.setItem(TOKEN_KEY, token);
export const removeToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// ─── 通用响应结构 ──────────────────────────────────────────────
export interface ApiResponse<T = unknown> {
  code: number;
  message: string;
  data: T;
}

export interface PageData<T = unknown> {
  list: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ─── Axios 实例 ───────────────────────────────────────────────
const request: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ─── 请求拦截器：自动注入 Token ───────────────────────────────
request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// ─── 响应拦截器：统一处理错误 ─────────────────────────────────
request.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const { code, message, data } = response.data;
    if (code === 0 || code === 200) {
      return { ...response, data: response.data };
    }
    // 业务错误
    return Promise.reject(new Error(message || '请求失败'));
  },
  (error: AxiosError<ApiResponse>) => {
    if (error.response) {
      const { status } = error.response;
      if (status === 401) {
        // Token 过期，清除登录态
        removeToken();
        window.dispatchEvent(new CustomEvent('auth:logout'));
      } else if (status === 403) {
        window.dispatchEvent(new CustomEvent('auth:forbidden'));
      }
    }
    return Promise.reject(error);
  },
);

export default request;

// ─── 子模块 re-export ─────────────────────────────────────────
export * from './crm';
export * from './im';
export * from './knowledge';
export * from './material';
export * from './talent';
export * from './user';
export * from './workflow';

// ─── 类型 re-export ───────────────────────────────────────────
export type { PaginationParams, SortParams, QueryParams } from './types/common';
export type {
  Talent, TalentList, TalentWorkPanel, TalentDetail,
  ChatMessage, ChatRequest, ChatResponse, TalentQueryParams,
} from './types/talent';
export type {
  UserInfo, LoginRequest, LoginResponse, UserListParams,
} from './types/user';
export type {
  NodeType, WorkflowNode, WorkflowEdge, Workflow,
  WorkflowInstance, WorkflowQueryParams,
} from './types/workflow';
